import * as React from 'react'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import Grid from '@material-ui/core/Grid'
import { Box, Button, Divider, Typography } from '@material-ui/core'

import { useDispatch, useSelector } from 'react-redux'
import Widget from '../Widget'
import Loading from '../helpers/Loading'
import { ErrorMessage } from '../../DashboardComponents/ErrorMessages/ErrorMsg'
import {
  fetchLearningCenter,
  selectLearningCenterData,
  selectLearningCenterStatus,
} from './LearningCenterSlice'
import learningCenterStyles from './learningCenterStyles'
import IconPicker from '../../../apps/AppComponents/IconPicker/IconPicker'

// eslint-disable-next-line no-unused-vars
function LearningCenter({
  className,
  title,
  showNote,
  noteMessage,
  notePosition,
  noteBackground,
  noteIcon,
  noteUrl,
  noteFontColor,
}) {
  dayjs.extend(localizedFormat)
  const dispatch = useDispatch()
  const widgetData = useSelector(selectLearningCenterData)
  const widgetCallState = useSelector(selectLearningCenterStatus)
  React.useEffect(() => {
    dispatch(fetchLearningCenter())
  }, [])
  return (
    <Widget className={className}>
      <Widget.Header title={title} category="default" />
      <Widget.Content
        showNote={showNote}
        noteMessage={noteMessage}
        notePosition={notePosition}
        noteBackground={noteBackground}
        noteIcon={noteIcon}
        noteUrl={noteUrl}
        noteFontColor={noteFontColor}
      >
        {renderLearningCenterState(widgetCallState, widgetData)}
      </Widget.Content>
    </Widget>
  )
}

function renderLearningCenterState(widgetCallState, widgetData) {
  // TODO: Handle Proper Error States
  switch (widgetCallState) {
    case 'fulfilled':
      return <Loaded widgetData={widgetData} />
    case 'pending':
      return <Loading />
    case 'error':
      return (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <ErrorMessage />
        </Grid>
      )
    default:
      return (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item></Grid>
        </Grid>
      )
  }
}

function Loaded(props) {
  const classes = learningCenterStyles()
  const { widgetData } = props
  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      className={classes.gridContainer}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-around"
        alignItems="center"
        spacing={1}
      >
        {widgetData.learningCenterLinks.slice(0, 2).map((button) => (
          <Grid item xs={6} md key={button.iconLabel + button.link}>
            <Button
              key={button.iconLabel + button.link}
              component="a"
              target="_blank"
              href={button.link}
              className={classes.buttonLink}
              style={{
                background: button.backgroundColor,
                color: button.color,
              }}
            >
              <Grid
                container
                direction="column"
                justifyContent="space-around"
                alignItems="center"
              >
                <Grid item>
                  <IconPicker iconName={button.icon} color="#000" size="3x" />
                </Grid>
                <Grid item>
                  <Typography variant="caption" className={classes.boldText}>
                    {button.iconLabel}
                  </Typography>
                </Grid>
              </Grid>
            </Button>
          </Grid>
        ))}
      </Grid>
      <Grid
        container
        direction="row"
        justifyContent="space-around"
        alignItems="center"
        spacing={1}
      >
        {widgetData.learningCenterLinks.slice(2, 4).map((button) => (
          <Grid item xs={6} md key={button.iconLabel + button.link}>
            <Button
              key={button.iconLabel + button.link}
              component="a"
              target="_blank"
              href={button.link}
              className={`${classes.allWorkshopsButton} ${classes.buttonLink}`}
              style={{
                background: button.backgroundColor,
                color: button.color,
              }}
            >
              <Grid
                container
                direction="column"
                justifyContent="space-around"
                alignItems="center"
              >
                <Grid item>
                  <IconPicker iconName={button.icon} color="#000" size="3x" />
                </Grid>
                <Grid item>
                  <Typography variant="caption" className={classes.boldText}>
                    {button.iconLabel}
                  </Typography>
                </Grid>
              </Grid>
            </Button>
          </Grid>
        ))}
      </Grid>
      <Grid item container>
        <Box className={classes.boxContainer}>
          <Divider variant="fullWidth" className={classes.divider} />
          <Typography variant="h5" className={classes.upcomingWorkshopsTitle}>
            Upcoming Workshops
          </Typography>
          <Divider variant="fullWidth" className={classes.divider} />
          <div className={classes.upcomingWorkShopListContainer}>
            {widgetData.workShopEvents.Data.map((event) => (
              <div key={event.title}>
                <div className={classes.eventContainer}>
                  <div className={classes.eventNameContainer}>
                    <Typography
                      component="a"
                      variant="body1"
                      href={
                        event.EventWebPageURL
                          ? event.EventWebPageURL
                          : event.EventRegistrationPageURL
                      }
                      target="_blank"
                      className={classes.eventName}
                    >
                      {event.EventName}
                    </Typography>
                  </div>
                  <div className={classes.eventDateContainer}>
                    <Typography variant="body1">
                      {dayjs(event.EventDate).format('dddd, LL [@] LT')}
                    </Typography>
                  </div>
                </div>
                <Divider variant="fullWidth" className={classes.divider} />
              </div>
            ))}
          </div>
        </Box>
      </Grid>
      <Grid item container>
        <div className={classes.allWorkshopsContainer}>
          <Button
            component="a"
            target="_blank"
            href={widgetData.viewAllWorkshopsLink.link}
            className={classes.allWorkshopsButton}
            style={{
              background: widgetData.viewAllWorkshopsLink.backgroundColor,
              color: widgetData.viewAllWorkshopsLink.color,
            }}
          >
            {widgetData.viewAllWorkshopsLink.title}
          </Button>
        </div>
      </Grid>
      <Grid item container>
        <div className={classes.footerLinksContainer}>
          {widgetData.learningCenterFooterLinks.map((footerButtonLink) => (
            <Button
              component="a"
              size="small"
              variant="outlined"
              href={footerButtonLink.link}
              className={classes.allWorkshopsFooterButton}
              style={{
                background: footerButtonLink.backgroundColor,
                color: footerButtonLink.color,
              }}
              target="_blank"
            >
              {footerButtonLink.title}
            </Button>
          ))}
        </div>
      </Grid>
    </Grid>
  )
}

export default LearningCenter
